import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AuthenticatedPage from '../auth/authenticatedPage';
import { RootStoreContext } from '../../stores/rootStore';
import { observer } from 'mobx-react-lite';
import { saveVisit } from '../../api/visitTrackerService';
import { PaidOrderDto } from '../../models/dtoTypes';

const CopyIcon = () => (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12">
        <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
);
const CheckIcon = ({ size = 12 }: { size?: number }) => (
    <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" width={size} height={size}>
        <polyline points="20,6 9,17 4,12"/>
    </svg>
);

const PaymentSuccess = () => {
    const rootStore = useContext(RootStoreContext);
    const { isLoggedIn } = rootStore.userStore;
    const { paymentId } = useParams();
    const orderStore = rootStore.orderStore;
    const [copiedCode, setCopiedCode] = useState<string | null>(null);
    const [copiedAll, setCopiedAll] = useState(false);

    useEffect(() => {
        if (!isLoggedIn) return;
        saveVisit('payment-success PaymentId: ' + paymentId);
        if (orderStore && isLoggedIn && paymentId !== undefined) {
            orderStore.checkPayment(parseInt(paymentId));
        }
    }, [isLoggedIn, orderStore, paymentId]);

    //once the payment is confirmed, clear the saved purchase state so the next purchase starts
    //fresh (the used discount code shouldn't linger in the form)
    useEffect(() => {
        if (orderStore.order && orderStore.order.error === null) {
            rootStore.purchaseStore.clearPurchaseState();
        }
    }, [orderStore.order, rootStore.purchaseStore]);

    const handleCopy = (code: string) => {
        navigator.clipboard?.writeText(code).catch(() => {});
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 1500);
    };

    const handleCopyAll = (codes: string[]) => {
        navigator.clipboard?.writeText(codes.join('\n')).catch(() => {});
        setCopiedAll(true);
        setTimeout(() => setCopiedAll(false), 1500);
    };

    return (
        <AuthenticatedPage>
            <div className="page-wrap">
                {orderStore.order === undefined ? (
                    <div className="page-center" style={{ flexDirection: 'column', gap: '16px' }}>
                        <div className="dcr-spinner" />
                        <p style={{ color: 'var(--muted)', fontSize: '.95rem' }}>Checking payment result…</p>
                    </div>
                ) : orderStore.order?.error !== null ? (
                    <div className="page-center">
                        <h1>Something went wrong</h1>
                        <p>{orderStore.order?.error}</p>
                    </div>
                ) : (
                    <div className="success-wrap">
                        <div className="result-icon success-icon">
                            <svg fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" width="30" height="30">
                                <polyline points="20,6 9,17 4,12"/>
                            </svg>
                        </div>

                        <h1>Thank you for your purchase!</h1>
                        <p className="success-lede">
                            {(orderStore.order?.numberOfSeats ?? 0) > 1
                                ? <>Your subscriptions are now active. Use the activation codes below to unlock <strong>Desktop Call Recorder Pro</strong> on each computer.</>
                                : <>Your subscription is now active. Use the activation code below to unlock <strong>Desktop Call Recorder Pro</strong>.</>}
                        </p>

                        <div className="order-chip">
                            <span className="order-chip-dot">
                                <CheckIcon size={13} />
                            </span>
                            Order <strong>#{(orderStore.order as PaidOrderDto).orderId}</strong> · Confirmation sent to your email
                        </div>

                        {'activationCodes' in (orderStore.order as PaidOrderDto) && (
                            <div className="codes-section">
                                <div className="section-label">
                                    Your activation codes
                                    <span className="badge">
                                        {(orderStore.order as PaidOrderDto).activationCodes.length} license{(orderStore.order as PaidOrderDto).activationCodes.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                                <div className="codes-card">
                                    {(orderStore.order as PaidOrderDto).activationCodes.map((code, i) => (
                                        <div key={i} className="code-row">
                                            <span className="code">{code}</span>
                                            <button
                                                className={`copy-btn${copiedCode === code ? ' copied' : ''}`}
                                                type="button"
                                                onClick={() => handleCopy(code)}
                                            >
                                                {copiedCode === code
                                                    ? <><CheckIcon /> Copied</>
                                                    : <><CopyIcon /> Copy</>}
                                            </button>
                                        </div>
                                    ))}
                                    <div className="codes-foot">
                                        <span>Codes also emailed to you</span>
                                        <button className="copy-all-btn" type="button" onClick={() => handleCopyAll((orderStore.order as PaidOrderDto).activationCodes)}>
                                            {copiedAll
                                                ? <><CheckIcon /> All copied</>
                                                : <><CopyIcon /> Copy all codes</>}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="steps">
                            <a href="https://download.desktopcallrecorder.com/Recorder3Setup.exe" className="step-card">
                                <div className="step-icon">
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="20" height="20">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                                    </svg>
                                </div>
                                <div className="step-text">
                                    <h3>Download the app</h3>
                                    <p>Recorder3Setup.exe · Windows 10/11</p>
                                </div>
                            </a>
                            <a href="/subscriptions" className="step-card">
                                <div className="step-icon">
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="20" height="20">
                                        <rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18M7 15h4"/>
                                    </svg>
                                </div>
                                <div className="step-text">
                                    <h3>Your subscriptions</h3>
                                    <p>Manage licenses &amp; invoices</p>
                                </div>
                            </a>
                        </div>

                        <p className="result-support">
                            Questions? Email <a href="mailto:team@desktopcallrecorder.com">team@desktopcallrecorder.com</a>
                        </p>
                    </div>
                )}
            </div>
        </AuthenticatedPage>
    );
};

export default observer(PaymentSuccess);
